import { useState } from "react";
import { Link2, Share2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function WhatsAppIcon(props) {
  return (<svg viewBox="0 0 24 24" fill="currentColor" {...props}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" /></svg>);
}
function FacebookIcon(props) {
  return (<svg viewBox="0 0 24 24" fill="currentColor" {...props}><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073Z" /></svg>);
}
function XIcon(props) {
  return (<svg viewBox="0 0 24 24" fill="currentColor" {...props}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117Z" /></svg>);
}

export function ShareButtons({ race, layout = "row" }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/race/${race.slug}`;
  const text = `🏃 ${race.name}\nFind it on YourRaceFinder`;

  const trackShare = (channel) => { api.post("/track", { event_type: "share", race_id: race.id, metadata: { channel } }).catch(() => {}); };

  const share = (channel) => {
    trackShare(channel);
    const enc = encodeURIComponent;
    let href = "";
    if (channel === "whatsapp") href = `https://wa.me/?text=${enc(text + "\n" + url)}`;
    else if (channel === "facebook") href = `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`;
    else if (channel === "x") href = `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`;
    window.open(href, "_blank", "noopener,width=640,height=640");
  };

  const copy = async () => {
    trackShare("copy");
    try { await navigator.clipboard.writeText(url); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Could not copy"); }
  };

  const native = async () => {
    if (navigator.share) {
      trackShare("native");
      try { await navigator.share({ title: race.name, text, url }); } catch {}
    } else { copy(); }
  };

  const items = [
    { key: "whatsapp", label: "WhatsApp", Icon: WhatsAppIcon, cls: "bg-[#25D366] text-white" },
    { key: "facebook", label: "Facebook", Icon: FacebookIcon, cls: "bg-[#1877F2] text-white" },
    { key: "x", label: "X", Icon: XIcon, cls: "bg-black text-white" },
  ];

  return (
    <div className={layout === "row" ? "flex items-center gap-3 flex-wrap" : "grid grid-cols-4 gap-2"}>
      {items.map(({ key, label, Icon, cls }) => (
        <button key={key} onClick={() => share(key)} data-testid={`race-share-${key}-button`}
          className={`flex flex-col items-center justify-center gap-1 ${cls} rounded-lg p-2.5 min-w-[64px] hover:opacity-90 transition-opacity`}>
          <Icon width={20} height={20} /><span className="text-[11px] font-semibold">{label}</span>
        </button>
      ))}
      <button onClick={copy} data-testid="race-share-copy-button"
        className="flex flex-col items-center justify-center gap-1 bg-slate-100 text-slate-700 rounded-lg p-2.5 min-w-[64px] hover:bg-slate-200 transition-colors">
        <Link2 size={20} /><span className="text-[11px] font-semibold">{copied ? "Copied" : "Copy"}</span>
      </button>
      <button onClick={native} data-testid="race-share-native-button"
        className="sm:hidden flex flex-col items-center justify-center gap-1 bg-slate-100 text-slate-700 rounded-lg p-2.5 min-w-[64px]">
        <Share2 size={20} /><span className="text-[11px] font-semibold">More</span>
      </button>
    </div>
  );
}
